import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import { getOptionIcon } from "./formIcons";
import { fieldShellClass, useFormValidation } from "./FormValidationContext";

export interface Option {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface SelectInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Option[];
  optional?: boolean;
  widthLabel?: string;
  widthSelect?: string;
  onFilledChange?: (filled: boolean) => void;
  stacked?: boolean;
  /** Force tile grid even for long lists */
  forceTiles?: boolean;
  /** Override validation error state */
  error?: boolean;
}

const CHIP_THRESHOLD = 10;

const SelectInput: React.FC<SelectInputProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  optional = false,
  onFilledChange,
  stacked = false,
  forceTiles = false,
  error,
}) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const { showErrors } = useFormValidation();

  const useTiles = forceTiles || options.length <= CHIP_THRESHOLD;
  const invalid =
    error ?? (showErrors && !optional && !String(value ?? "").trim());

  useEffect(() => {
    if (onFilledChange) {
      onFilledChange(optional || value !== "");
    }
  }, [value, optional, onFilledChange]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const emit = (next: string) => {
    onChange({
      target: { name, value: next },
    } as React.ChangeEvent<HTMLSelectElement>);
    if (!optional && onFilledChange) {
      onFilledChange(next !== "");
    }
  };

  const selected = options.find((o) => o.value === value);
  const selectedIcon =
    selected?.icon ??
    (selected ? getOptionIcon(selected.value, selected.label) : null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, query]);

  const labelEl = (
    <label
      htmlFor={id}
      className={`text-xs font-medium ${
        invalid ? "text-[var(--cu-danger)]" : "text-[var(--cu-muted)]"
      } ${stacked ? "mb-1.5 block" : "shrink-0 pt-1.5 md:w-[32%]"}`}
    >
      {label}
      {!optional && <span className="ml-1 text-[var(--cu-danger)]">*</span>}
      {optional && (
        <span className="ml-1.5 text-[11px] font-normal text-[var(--cu-subtle)]">
          optional
        </span>
      )}
      {invalid && (
        <span className="ml-1.5 text-[11px] font-normal text-[var(--cu-danger)]">
          Required
        </span>
      )}
    </label>
  );

  const tilePicker = (
    <div
      id={id}
      role="listbox"
      aria-label={label}
      className={`grid min-w-0 gap-1.5 ${
        stacked ? "w-full" : "flex-1"
      } ${
        options.length <= 3
          ? "grid-cols-2 sm:grid-cols-3"
          : options.length <= 6
            ? "grid-cols-2 sm:grid-cols-3"
            : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
      }`}
    >
      {optional && (
        <button
          type="button"
          role="option"
          aria-selected={!value}
          onClick={() => emit("")}
          className={`flex min-h-[3.25rem] flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2 text-center transition-colors ${
            !value
              ? "border-[var(--cu-accent)] bg-[var(--cu-accent-soft)] text-[var(--cu-accent)]"
              : "border-[var(--cu-border)] bg-[var(--cu-input)] text-[var(--cu-subtle)] hover:border-[var(--cu-border-hover)]"
          }`}
        >
          <FiX className="h-4 w-4" />
          <span className="text-[10px] font-medium leading-tight">None</span>
        </button>
      )}

      {options.map((opt) => {
        const active = value === opt.value;
        const icon = opt.icon ?? getOptionIcon(opt.value, opt.label);
        const letter = (opt.label.trim()[0] || "?").toUpperCase();
        return (
          <button
            key={opt.value}
            type="button"
            role="option"
            aria-selected={active}
            title={opt.label}
            onClick={() => emit(opt.value)}
            className={`relative flex min-h-[3.25rem] flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2 text-center transition-colors ${
              active
                ? "border-[var(--cu-accent)] bg-[var(--cu-accent-soft)] text-[var(--cu-text)]"
                : "border-[var(--cu-border)] bg-[var(--cu-input)] text-[var(--cu-muted)] hover:border-[var(--cu-border-hover)] hover:text-[var(--cu-text)]"
            }`}
          >
            {active && (
              <span className="absolute right-1.5 top-1.5 text-[var(--cu-accent)]">
                <FiCheck className="h-3 w-3" strokeWidth={3} />
              </span>
            )}
            {icon ? (
              <span
                className={
                  active
                    ? "text-[var(--cu-accent)]"
                    : "text-[var(--cu-subtle)]"
                }
              >
                {icon}
              </span>
            ) : (
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold ${
                  active
                    ? "bg-[var(--cu-accent)] text-white"
                    : "bg-[var(--cu-elevated)] text-[var(--cu-muted)]"
                }`}
              >
                {letter}
              </span>
            )}
            <span className="line-clamp-2 text-[10px] font-medium leading-tight">
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );

  const listPicker = (
    <div
      ref={rootRef}
      className={`relative min-w-0 ${stacked ? "w-full" : "flex-1"}`}
    >
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-2.5 rounded-xl border bg-[var(--cu-input)] px-3 py-2.5 text-left text-sm transition-colors ${
          open
            ? "border-[var(--cu-accent)]"
            : "border-[var(--cu-border)] hover:border-[var(--cu-border-hover)]"
        }`}
      >
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
            selected
              ? "bg-[var(--cu-accent-soft)] text-[var(--cu-accent)]"
              : "bg-[var(--cu-elevated)] text-[var(--cu-subtle)]"
          }`}
        >
          {selectedIcon ?? <FiChevronDown className="h-4 w-4" />}
        </span>
        <span className="min-w-0 flex-1 truncate text-[var(--cu-text)]">
          {selected?.label ?? (
            <span className="text-[var(--cu-subtle)]">Choose an option</span>
          )}
        </span>
        <FiChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--cu-subtle)] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-40 mt-1.5 overflow-hidden rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] shadow-xl">
          {options.length > 6 && (
            <div className="flex items-center gap-2 border-b border-[var(--cu-border)] px-3 py-2">
              <FiSearch className="h-3.5 w-3.5 text-[var(--cu-subtle)]" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full bg-transparent text-sm text-[var(--cu-text)] outline-none placeholder:text-[var(--cu-placeholder)]"
              />
            </div>
          )}

          <div
            role="listbox"
            className="max-h-56 overflow-y-auto p-1.5"
          >
            {optional && (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                onClick={() => {
                  emit("");
                  setOpen(false);
                  setQuery("");
                }}
                className={`mb-0.5 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                  !value
                    ? "bg-[var(--cu-accent-soft)] text-[var(--cu-accent)]"
                    : "text-[var(--cu-muted)] hover:bg-[var(--cu-elevated)]"
                }`}
              >
                <FiX className="h-4 w-4" />
                Clear selection
              </button>
            )}

            {filtered.length === 0 && (
              <p className="px-2.5 py-3 text-center text-xs text-[var(--cu-subtle)]">
                No matches
              </p>
            )}

            {filtered.map((opt) => {
              const active = value === opt.value;
              const icon = opt.icon ?? getOptionIcon(opt.value, opt.label);
              const letter = (opt.label.trim()[0] || "?").toUpperCase();
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    emit(opt.value);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`mb-0.5 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                    active
                      ? "bg-[var(--cu-accent-soft)] text-[var(--cu-text)]"
                      : "text-[var(--cu-muted)] hover:bg-[var(--cu-elevated)] hover:text-[var(--cu-text)]"
                  }`}
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold ${
                      active
                        ? "bg-[var(--cu-accent)] text-white"
                        : "bg-[var(--cu-input)] text-[var(--cu-subtle)]"
                    }`}
                  >
                    {icon ?? letter}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                  {active && (
                    <FiCheck className="h-3.5 w-3.5 shrink-0 text-[var(--cu-accent)]" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const control = useTiles ? tilePicker : listPicker;

  return (
    <div className={fieldShellClass(invalid)} data-invalid={invalid || undefined}>
      {stacked ? (
        <div>
          {labelEl}
          {control}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 md:flex-row md:items-start md:gap-4">
          {labelEl}
          {control}
        </div>
      )}
    </div>
  );
};

export default SelectInput;
